// Dev/QA helper: append ?FORCE_DATE=YYYY-MM-DD (optionally with a time, e.g.
// 2026-09-25 08:00) to any URL to evaluate all date-gated logic (e.g. the
// association results tab / canEditResults) as if "now" were that LOCAL
// moment. This lets testers preview a phase without editing
// CAMPAIGN_PHASE_DATES. A date without a time defaults to that day at 12:00.
//
// The value is persisted in session storage with a session-cookie fallback
// so it survives client-side navigation (Gatsby drops query params on
// internal links). Pass ?FORCE_DATE= (empty) to clear it.
//
// Available in production too. Falls back to the real clock when nothing is
// set or the value is unparseable.
//
// Examples:
//   ?FORCE_DATE=2026-09-24        -> results phase (24.09, midday)
//   ?FORCE_DATE=2026-09-24 08:00  -> results phase, early in the window
//   ?FORCE_DATE=                  -> clear the override

const FORCE_DATE_KEY = "__force_date__";

const setCookie = (cname: string, cvalue: string): void => {
  // Session cookie (no `expires`), cleared when the browser closes.
  document.cookie = `${cname}=${cvalue};path=/`;
};

const getCookie = (cname: string): string => {
  const name = `${cname}=`;
  // Decode per entry, not the whole header: one malformed cookie anywhere on the domain would
  // otherwise make decodeURIComponent throw and take the whole lookup down with it.
  const parts = document.cookie.split(";");
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    if (part.indexOf(name) === 0) {
      const raw = part.substring(name.length);
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
  }
  return "";
};

const removeCookie = (cname: string): void => {
  document.cookie = `${cname}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

// iOS "smart punctuation" silently rewrites typed hyphens as en/em dashes
// (and other Unicode dashes), so a date pasted/typed on iPhone can arrive as
// "2026-09\u201324". Normalize every Unicode dash back to an ASCII hyphen so
// the parser accepts it.
const normalizeDashes = (value: string): string => value.replace(/[\u2010-\u2015\u2212]/g, "-");

export const parseForcedNow = (value: string): Date | undefined => {
  const trimmed = normalizeDashes(value.trim());
  if (!trimmed) {
    return undefined;
  }

  // Accept "YYYY-MM-DD" optionally followed by a time "H:mm" or "HH:mm"
  // (separated by a space or "T"). Built from components and interpreted as
  // LOCAL time, so no timezone offset is needed in the URL and single-digit
  // hours (e.g. "9:00") work. A bare date defaults to 12:00 local.
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?$/);
  if (!match) {
    return undefined;
  }

  const [, year, month, day, hour = "12", minute = "00"] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const getStoredForcedDate = (): string => {
  try {
    return sessionStorage.getItem(FORCE_DATE_KEY) || getCookie(FORCE_DATE_KEY);
  } catch {
    return getCookie(FORCE_DATE_KEY);
  }
};

export const clearForcedDate = (): void => {
  try {
    sessionStorage.removeItem(FORCE_DATE_KEY);
  } catch {
    // Cookie fallback still works when browser storage is unavailable.
  }
  removeCookie(FORCE_DATE_KEY);
};

export const persistForcedDate = (value: string): string => {
  const stored = normalizeDashes(value.trim()).replace(" ", "T");
  if (!stored || !parseForcedNow(stored)) {
    clearForcedDate();
    return "";
  }

  try {
    sessionStorage.setItem(FORCE_DATE_KEY, stored);
  } catch {
    // Cookie fallback still works when browser storage is unavailable.
  }
  setCookie(FORCE_DATE_KEY, stored);
  return stored;
};

// Resolves the active forced-date string, giving the ?FORCE_DATE query param
// precedence and mirroring it into session storage and a fallback cookie.
const getForcedDateString = (): string => {
  const param = new URLSearchParams(window.location.search).get("FORCE_DATE");

  if (param === null) {
    return getStoredForcedDate();
  }

  // Empty param explicitly clears the override.
  if (param.trim() === "") {
    clearForcedDate();
    return "";
  }

  return persistForcedDate(param);
};

/**
 * Captures `?FORCE_DATE` into session storage + cookie without needing anything to read the clock.
 *
 * Persisting used to happen only as a side effect of `getNow()`, so the override was silently
 * dropped on every page that has no date-gated logic — the login screen, the static pages, and the
 * association overview (its only `getNow()` caller returns early outside production). Landing there
 * with `?FORCE_DATE=…` stored nothing, and the value was gone by the time the user navigated on,
 * because Gatsby drops query params from internal links. Called once per route from gatsby-browser.
 */
export const syncForcedDateFromUrl = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  getForcedDateString();
};

/** The current moment, honoring a `?FORCE_DATE` override when present. */
export const getNow = (): Date => {
  if (typeof window === "undefined") {
    return new Date();
  }

  const forced = parseForcedNow(getForcedDateString());
  return forced ?? new Date();
};
